import React from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import {
    VolumeProjectionSummary,
    getStatusBannerBackgroundColor,
    getStatusAccentColor,
    formatWholeNumber,
} from '../utils';

interface ShiftProgressBannerProps {
    /** Shared volume projection (same object the Maestro cards render). */
    projection: VolumeProjectionSummary;
}


/**
 * The full-width pace banner shown above the shift timeline. Renders the same
 * projection the Maestro VOLUME card shows — e.g.
 * "6,000 UNITS BEHIND PACE — Projecting 94,000 vs plan 100,000 (94%)" — tinted
 * and colored by the server-computed actual-vs-plan status.
 *
 * When there is no projection yet (no ALPS data or shift window), it falls back
 * to a plain status-tinted band with no text.
 */
const ShiftProgressBanner: React.FC<ShiftProgressBannerProps> = ({ projection }) => {
    const { comparisonStatus, projectedEos, targetValue, projectedPercent, paceDelta } = projection;

    const backgroundColor = getStatusBannerBackgroundColor(comparisonStatus);
    const textColor = getStatusAccentColor(comparisonStatus);

    const hasProjection =
        projectedEos != null && targetValue != null && projectedPercent != null && paceDelta != null;

    // "BEHIND" when the projection lands under plan, "AHEAD OF" when over, "ON"
    // when exactly on plan.
    const paceLabel =
        paceDelta == null || paceDelta === 0
            ? 'ON PACE'
            : `${formatWholeNumber(Math.abs(paceDelta))} UNITS ${paceDelta < 0 ? 'BEHIND' : 'AHEAD OF'} PACE`;

    return (
        <Box
            data-testid="shift-progress-banner"
            height={36}
            width="100%"
            backgroundColor={backgroundColor}
            type="outline"
        >
            {hasProjection && (
                <Box spacingInset="200" height="100%">
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                        }}
                    >
                        <Text type="b200">
                            <span style={{ color: textColor, fontWeight: 700 }}>
                                {paceLabel} — Projecting {formatWholeNumber(projectedEos!)} vs plan{' '}
                                {formatWholeNumber(targetValue!)} ({projectedPercent}%)
                            </span>
                        </Text>
                    </div>
                </Box>
            )}
        </Box>
    );
};

export default ShiftProgressBanner;
